import React, { useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Users,
  Coins,
  HeartHandshake,
  Facebook,
  Twitter,
  Instagram,
  MessageCircle,
  Clock,
  CheckCircle,
  HelpCircle,
  Send,
  Globe,
  Shield,
  CreditCard,
} from "lucide-react";
import { Helmet } from "react-helmet";

// ─── SEO Component ──────────────────────────────────────────────────────────────
function SEOHead() {
  useEffect(() => {
    // Page Title
    document.title = "Contact Us - DonatePE";
    
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    
    // Meta Description
    setMeta("description", "Get in touch with the DonatePE team for support, queries, or partnerships. Contact our crowdfunding and donation platform for help with starting fundraisers, donations, and more.");
    
    // Robots
    setMeta("robots", "index, follow");
    
    // Charset
    let charset = document.querySelector('meta[charset="UTF-8"]');
    if (!charset) {
      charset = document.createElement("meta");
      charset.setAttribute("charset", "UTF-8");
      document.head.insertBefore(charset, document.head.firstChild);
    }
    
    // Viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0");
      document.head.appendChild(viewport);
    }
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://donatepe.org/contact");
    
    // Open Graph Tags
    setMeta("og:title", "Contact Us - DonatePE", true);
    setMeta("og:description", "Get in touch with the DonatePE team for support, queries, or partnerships. Contact our crowdfunding and donation platform for help.", true);
    setMeta("og:url", "https://donatepe.org/contact", true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://donatepe.org/logo.png", true);
    
    // Twitter Card Tags
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Contact Us - DonatePE");
    setMeta("twitter:description", "Get in touch with the DonatePE team for support, queries, or partnerships. Contact our crowdfunding and donation platform for help.");
    setMeta("twitter:image", "https://donatepe.org/logo.png");
  }, []);
  
  return null;
}

const ContactUs = () => {
  // Add WhatsApp submit handler
  const handleWhatsAppSubmit = () => {
    // Get form values
    const fullName = document.getElementById('fullName')?.value || '';
    const email = document.getElementById('email')?.value || '';
    const purpose = document.getElementById('purpose')?.value || '';
    const message = document.getElementById('message')?.value || '';
    
    // Validate required fields
    if (!fullName || !email || !message) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Format the message for WhatsApp
    const whatsappMessage = `*New Contact Form Submission*%0A%0A` +
      `*Name:* ${fullName}%0A` +
      `*Email:* ${email}%0A` +
      `*Purpose:* ${purpose || 'Not specified'}%0A` +
      `*Message:* ${message}`;
    
    // Your WhatsApp number (replace with your actual number)
    const phoneNumber = '917799499667'; // Replace with your actual WhatsApp number
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${whatsappMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
  };


  // Add this inside your ContactUs component
const handleSubmit = (e) => {
  e.preventDefault();
  
  // Get form values
  const fullName = document.getElementById('fullName')?.value || '';
  const email = document.getElementById('email')?.value || '';
  const purpose = document.getElementById('purpose')?.value || '';
  const message = document.getElementById('message')?.value || 'No message';
  
  // Validate Full Name (letters only)
  const nameRegex = /^[A-Za-z\s]+$/;
  if (!fullName) {
    alert('Please enter your full name');
    return;
  }
  if (!nameRegex.test(fullName)) {
    alert('Full name should contain only letters and spaces');
    return;
  }
  
  // Validate Email
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!email) {
    alert('Please enter your email address');
    return;
  }
  if (!emailRegex.test(email)) {
    alert('Please enter a valid email address');
    return;
  }
  
  // Validate Purpose
  if (!purpose) {
    alert('Please select a purpose');
    return;
  }
  
  // Format the message for WhatsApp
  const whatsappMessage = `*New Contact Form Submission*%0A%0A` +
    `*Name:* ${fullName}%0A` +
    `*Email:* ${email}%0A` +
    `*Purpose:* ${purpose}%0A` +
    `*Message:* ${message}`;
  
  // Your WhatsApp number (replace with your actual number)
  const phoneNumber = '917799499667'; // Replace with your actual WhatsApp number
  
  // Create WhatsApp URL
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${whatsappMessage}`;
  
  // Open WhatsApp in new tab
  window.open(whatsappUrl, '_blank');
};

  return (
    <>

    

<Helmet>
  <title>DonatePe | Raise Funds for Medical & Social Causes in India</title>

  <meta
    name="description"
    content="DonatePe is a trusted crowdfunding platform in India to raise funds for medical emergencies, personal needs, and social causes. Start fundraising or donate easily."
  />

  <meta
    name="keywords"
    content="donate, crowdfunding India, medical fundraiser, charity, donatepe"
  />

  {/* Open Graph */}
  <meta property="og:title" content="DonatePe | Online Fundraising Platform" />
  <meta
    property="og:description"
    content="Raise funds for causes you care about or support others through DonatePe."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://donatepe.org/" />

  {/* Recommended additions */}
  <meta property="og:image" content="https://donatepe.org/preview.png" />
  <link rel="canonical" href="https://donatepe.org/" />
</Helmet>
      <SEOHead />
      {/* Breadcrumb Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://donatepe.org"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact Us",
              "item": "https://donatepe.org/contact"
            }
          ]
        })}
      </script>
      <div className="w-full font-sans antialiased bg-white">
      
      {/* ===== STYLES ===== */}
      <style>
        {`
        @keyframes gentleFade {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes softPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        
        .animate-gentle {
          animation: gentleFade 0.6s ease forwards;
        }
        
        .animate-soft-pulse {
          animation: softPulse 3s ease-in-out infinite;
        }
        
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .delay-4 { animation-delay: 0.4s; }
        .opacity-0 { opacity: 0; }
        `}
      </style>

      {/* ===== HERO SECTION WITH DONATEPE BRANDING ===== */}
      <section className="relative pt-16 pb-10 md:pt-20 md:pb-12 overflow-hidden">
        {/* Subtle gradient orbs */}
        <div className="absolute top-20 right-20 w-80 h-80 bg-blue-50 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-emerald-50 rounded-full blur-3xl opacity-40"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Brand badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-full border border-blue-100 mb-4 opacity-0 animate-gentle shadow-sm">
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-soft-pulse"></span>
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-soft-pulse" style={{ animationDelay: '0.5s' }}></span>
            </div>
            <span className="text-xs font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
              ✦ DONATEPE SUPPORT
            </span>
          </div>
          
          {/* Headline */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 tracking-tight mb-4 opacity-0 animate-gentle">
            We're here to help, 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 block mt-1">
              every step of the way.
            </span>
          </h1>
          
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed opacity-0 animate-gentle delay-1">
            Whether you're starting a fundraiser, managing donations, or just exploring — 
            the <span className="font-semibold text-blue-600">DonatePe</span> team is always ready to support you.
          </p>
          
          {/* Quick contact options */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6 opacity-0 animate-gentle delay-2">
            
            <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-full">
              <Mail size={14} className="text-emerald-600" />
              <span className="text-xs text-gray-600">info@donatepe.org</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAIN CONTACT SECTION ===== */}
      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* RIGHT SIDE - FORM - This appears ABOVE on mobile (order-1) */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-md p-5 md:p-6 order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center text-white">
                  <Send size={16} />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Send us a message</h3>
              </div>
              
              <form className="space-y-4" id="contactForm" onSubmit={handleSubmit}>
                {/* Full Name - Required, letters only */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="fullName" className="text-xs font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <input
                    type="text"
                    id="fullName"
                    placeholder="Enter your full name"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    pattern="[A-Za-z\s]+"
                    title="Please enter only letters and spaces"
                    required
                  />
                </div>
                
                {/* Email Address - Required, email format */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="email" className="text-xs font-medium text-gray-700">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email address"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                    title="Please enter a valid email address"
                    required
                  />
                </div>
                
                {/* Purpose - Required */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="purpose" className="text-xs font-medium text-gray-700">
                      Purpose <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <select
                      id="purpose"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-600 appearance-none bg-white"
                      required
                    >
                      <option value="" disabled selected>Select a purpose</option>
                      <option value="General Query">General Query</option>
                      <option value="Start a Fundraiser">Start a Fundraiser</option>
                      <option value="Donation Issue">Donation Issue</option>
                      <option value="Partnership Opportunity">Partnership Opportunity</option>
                      <option value="Technical Support">Technical Support</option>
                    </select>
                    <div className="absolute right-2 top-2 pointer-events-none">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Message - Optional */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="message" className="text-xs font-medium text-gray-700">
                      Message
                    </label>
                    <span className="text-xs text-gray-400">Optional</span>
                  </div>
                  <textarea
                    id="message"
                    rows="4"
                    placeholder="Tell us how we can help you..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                  ></textarea>
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white font-medium py-2.5 text-sm rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.554 4.128 1.522 5.862L.053 23.1c-.112.442.293.847.735.735l5.238-1.469C7.872 23.446 9.872 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.846 0-3.594-.498-5.094-1.371l-.366-.22-3.765 1.056 1.058-3.765-.22-.366A9.943 9.943 0 012 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/>
                  </svg>
                  Send via WhatsApp
                </button>
                
                <p className="text-xs text-gray-400 text-center mt-3">
                  We'll get back to you within 4 hours. Your data is secure.
                </p>
              </form>
            </div>

            {/* LEFT SIDE - CONTACT INFO - This appears BELOW on mobile (order-2) */}
            <div className="space-y-5 order-2 lg:order-1">
              {/* Section header */}
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full mb-3">
                  <MessageCircle size={14} className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-600">Get in touch</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-3">
                  Let's talk about your 
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 block">
                    fundraising journey.
                  </span>
                </h2>
                <p className="text-gray-600 text-sm md:text-base">
                  At <span className="font-semibold text-blue-600">DonatePe</span>, we believe in personal support. 
                  Whether you need help starting a campaign, managing donations, or exploring partnerships — we're just a message away.
                </p>
              </div>
              
              {/* Contact cards */}
              <div className="space-y-3">
                
                
                <div className="bg-white rounded-xl border border-gray-100 p-4 hover:border-emerald-200 hover:shadow-sm transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                      <Mail size={18} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">Email us anytime</h4>
                      <p className="text-xs text-gray-500">24/7 support, replies within 4 hours</p>
                      <p className="text-emerald-600 font-medium text-sm mt-0.5">info@donatepe.org</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Social media & trust */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-5 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 text-sm">Connect with DonatePe</h4>
                  <span className="text-xs text-gray-400">50K+ followers</span>
                </div>
                <div className="flex gap-2">
                  {[
                    { 
                      icon: Facebook, 
                      label: 'Facebook',
                      link: "https://www.facebook.com/share/1NXQTRboXz/"
                    },
                    { 
                      icon: Twitter, 
                      label: 'Twitter',
                      link: "https://twitter.com/donatepeindia"
                    },
                    { 
                      icon: Instagram, 
                      label: 'Instagram',
                      link: "https://www.instagram.com/donatepeindia?utm_source=qr&igsh=NGFnc2Q1Z2w0YnBw"
                    }
                  ].map((social, i) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={i}
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center text-black hover:bg-black hover:text-white transition-all duration-300 cursor-pointer"
                        title={social.label}
                      >
                        <Icon size={16} />
                      </a>
                    );
                  })}
                </div>
                
                {/* Trust badges */}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <Shield size={12} className="text-black" />
                    <span className="text-xs text-gray-500">Secure</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={12} className="text-black" />
                    <span className="text-xs text-gray-500">24/7 Support</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle size={12} className="text-black" />
                    <span className="text-xs text-gray-500">Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SUPPORT CHANNELS ===== */}
      <section className="py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-2">
              Other ways to reach us
            </h2>
            <p className="text-sm text-gray-500">
              Choose the channel that works best for you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                title: 'Live Chat',
                description: 'Chat with our support team in real-time',
                availability: 'Available 24/7',
                icon: MessageCircle,
                color: 'blue',
                action: ''
              },
              {
                title: 'Email Support',
                description: 'Send us a detailed message anytime',
                availability: 'Reply within 4 hours',
                icon: Mail,
                color: 'emerald',
                action: ''
              },
              {
                title: 'Phone Support',
                description: 'Speak directly with our team',
                availability: 'Mon-Sat, 9AM-8PM',
                icon: Phone,
                color: 'amber',
                action: ''
              }
            ].map((channel, i) => {
              const Icon = channel.icon;
              return (
                <div key={i} className="bg-white p-5 rounded-xl border border-gray-100 text-center hover:shadow-sm transition-all duration-300 group">
                  <div className={`w-12 h-12 mx-auto mb-3 bg-${channel.color}-50 rounded-xl flex items-center justify-center text-${channel.color}-600 group-hover:scale-110 transition-transform`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="text-base font-medium text-gray-900 mb-1">{channel.title}</h3>
                  <p className="text-xs text-gray-500 mb-1">{channel.description}</p>
                  <p className="text-xs text-gray-400 mb-2">{channel.availability}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-emerald-600"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-white mb-2">
            Ready to start your DonatePe journey?
          </h2>
          <p className="text-blue-100 mb-5 text-base max-w-2xl mx-auto">
            Join thousands of campaigners who've raised crores for what matters.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/start-fundraiser"
              className="group px-6 py-3 bg-white hover:bg-gray-100 text-gray-900 rounded-lg text-sm font-medium shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              <span className="flex items-center gap-2">
                Start your fundraiser
                <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </a>
          </div>
          
          <p className="text-xs text-blue-200 mt-4">
            ✓ Free forever · ✓ Instant withdrawals · ✓ 24/7 support
          </p>
        </div>
      </section>

      {/* Grid pattern style */}
      <style>{`
        .bg-grid-pattern {
          background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
      
    </div>
    </>
  );
};

export default ContactUs;